import classNames from 'classnames';
import {
	forwardRef,
	useImperativeHandle,
	useRef,
	useState,
	useEffect,
	cloneElement,
	isValidElement,
	Children,
} from 'react';
import { OverlayScrollbarsComponent, OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';

import styles from './index.module.less';
import type { IBaseOverlayScrollbar } from './interface';
import { isFunction } from 'lodash-es';
import { BaseOverlayScrollbarContext } from './context';

// 目前只实现了最基本的展示以及修改样式功能，
// 若要支持更多事件可自行参照修改 https://github.com/KingSora/OverlayScrollbars

export const BaseOverlayScrollbar = forwardRef<
	IBaseOverlayScrollbar.Forward,
	IBaseOverlayScrollbar.Props
>((props, ref) => {
	const {
		autoDisappear = true,
		appearDuration = 0,
		children,
		className,
		style,
		onInitialized,
		onScroll,
		options,
		scrollHandle,
		scrollToEnd,
		checkOverallScrollable = false, //必须有最外层元素包裹
		smoothScroll = false, // 新增属性，用于控制是否启用平滑滚动
	} = props;

	const [scrollAppear, setScrollAppear] = useState(false);
	const [isScrollable, setIsScrollable] = useState(false);
	const canScrollable = useRef<boolean | undefined>();
	const container = useRef<HTMLDivElement>();
	const childrenRef = useRef<HTMLDivElement>();
	const osRef = useRef<OverlayScrollbarsComponentRef>(null);
	const timerRef = useRef<NodeJS.Timeout>();
	const mergeClassName = classNames(className, styles['scrollbar'], {
		[styles['hide']]: !scrollAppear,
	});

	// 获取滚动的容器
	const getScrollOffsetElement = () => {
		const { current } = osRef;
		const osInstance = current?.osInstance();

		if (!osInstance) {
			return;
		}

		const { scrollOffsetElement } = osInstance.elements();
		return scrollOffsetElement as HTMLDivElement;
	};

	const handleScroll: typeof onScroll = (scrollbar, event) => {
		handleMouseEnter();

		const { scrollOffsetElement } = scrollbar.elements();
		const isHorizontalScroll = scrollOffsetElement.scrollWidth > scrollOffsetElement.clientWidth;
		const isVerticalScroll = scrollOffsetElement.scrollHeight > scrollOffsetElement.clientHeight;

		const isAtLeft = isHorizontalScroll && scrollOffsetElement.scrollLeft === 0;
		const isAtRight =
			isHorizontalScroll &&
			scrollOffsetElement.scrollLeft + scrollOffsetElement.clientWidth >=
				scrollOffsetElement.scrollWidth;
		const isAtTop = isVerticalScroll && scrollOffsetElement.scrollTop === 0;
		const isAtBottom =
			isVerticalScroll &&
			Math.ceil(scrollOffsetElement.scrollTop + scrollOffsetElement.clientHeight) >=
				scrollOffsetElement.scrollHeight;
		const isAtMiddle = !isAtTop && !isAtBottom && !isAtLeft && !isAtRight;
		if (isAtLeft || isAtRight || isAtTop || isAtBottom || isAtMiddle) {
			if (isFunction(scrollToEnd)) {
				const position = isAtLeft
					? 'left'
					: isAtRight
						? 'right'
						: isAtTop
							? 'top'
							: isAtBottom
								? 'bottom'
								: 'middle';
				scrollToEnd(position);
			}
		}

		isFunction(onScroll) && onScroll(scrollbar, event);
	};

	const handleMouseEnter = () => {
		if (autoDisappear) {
			setScrollAppear(true);
			clearTimeout(timerRef.current);
		}
	};

	const handleMouseLeave = () => {
		timerRef.current = setTimeout(() => {
			setScrollAppear(false);
		}, appearDuration);
	};

	const handleInitialize: typeof onInitialized = (scrollbar) => {
		const element = scrollbar.elements().scrollOffsetElement;
		container.current = element as HTMLDivElement;

		// 检查内容宽度是否大于容器宽度
		checkScrollable();

		isFunction(onInitialized) && onInitialized(scrollbar);
	};

	const scrollTo = (position: 'up' | 'down', speed = 5) => {
		const element = getScrollOffsetElement();
		if (element) {
			if (smoothScroll) {
				element.scrollTo({
					top: element.scrollTop + (position === 'up' ? -speed : speed),
					behavior: 'smooth',
				});
			} else {
				element.scrollTop += position === 'up' ? -speed : speed;
			}
		}
	};

	const scrollToTop = (value: number, smoothScroll = false) => {
		const element = getScrollOffsetElement();
		if (element) {
			element.scrollTo({ top: value, behavior: smoothScroll ? 'smooth' : 'auto' });
		}
	};

	useImperativeHandle(ref, () => {
		const element = getScrollOffsetElement();

		return {
			scroll: (callback) => {
				element && callback && callback(element);
			},
			scrollUp: (speed?: number | undefined) => scrollTo('up', speed),
			scrollDown: (speed?: number | undefined) => scrollTo('down', speed),
			setScrollVertical: (value: number, smoothScroll?: boolean | undefined) =>
				scrollToTop(value, smoothScroll),
			getScrollContainer: () => element,
			getScrollable: () => canScrollable.current,
			getContainerHeight: () => (element ? element.clientHeight : 0),
			getVerticalScroll: () => (element ? element.scrollTop : 0),
		};
	}, [getScrollOffsetElement]);

	const checkScrollable = () => {
		if (childrenRef.current && container.current) {
			const canScroll =
				childrenRef.current.scrollWidth > container.current.clientWidth ||
				childrenRef.current.scrollHeight > container.current.clientHeight;
			canScrollable.current = canScroll;
			if (isFunction(scrollToEnd) && !canScroll) {
				scrollToEnd('none');
			}
			if (canScroll !== isScrollable) {
				setIsScrollable(canScroll);
				if (isFunction(scrollHandle)) {
					scrollHandle(canScroll);
				}
			}
		}
	};

	useEffect(() => {
		const handleResize = () => {
			if (childrenRef) {
				checkScrollable();
			}
		};

		window.addEventListener('resize', handleResize);
		handleResize();

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [isScrollable]);

	useEffect(() => {
		const containerElement = container.current;
		const childrenElement = childrenRef.current;
		if (containerElement && childrenElement) {
			const resizeObserver = new ResizeObserver(() => {
				checkScrollable();
			});

			// 观察容器和子元素
			resizeObserver.observe(containerElement);
			resizeObserver.observe(childrenElement);
			return () => {
				resizeObserver.disconnect();
			};
		}
	}, [childrenRef.current, container.current]);

	return (
		<OverlayScrollbarsComponent
			className={mergeClassName}
			events={{
				scroll: handleScroll,
				initialized: handleInitialize,
			}}
			options={options}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			ref={osRef}
			style={style}
		>
			<BaseOverlayScrollbarContext.Provider value={container}>
				{checkOverallScrollable && isValidElement(children) && Children.count(children) === 1
					? cloneElement(children as React.ReactElement, { ref: childrenRef })
					: children}
			</BaseOverlayScrollbarContext.Provider>
		</OverlayScrollbarsComponent>
	);
});
